import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdminHeader from '../Layout/AdminHeader';
import AdminSidebar from '../Layout/AdminSidebar';
import landingPageAPI from '../../services/landingPage';
import { uploadImageToS3 } from '../../services/uploads';
import { getLogoUploadErrorMessage, validateLogoFile } from '../../utils/logoUpload';
import { HomeBody, HomeHeader } from '../Landing/HomePageView';
import { EText, fieldId, setAt } from '../Landing/homeEditables';
import BlocksManager from './BlocksManager';
import '../Dashboard/Dashboard.css';
import '../Landing/LandingPage.css';
import './LandingPageEditor.css';

// SuperAdmin editor for the public home page (Sprint 7 "Home Template"), laid out like the
// Employer Template Editor: a top bar, a section bar to reorder / hide / restore sections, and
// the page itself, edited in place. The canvas uses the same renderer as the public page.
// Everything is plain text; the server validates it again (server/utils/landingPageContent.js).
// The on/off switch takes effect straight away, separately from Save: off sends `/` to login.

const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const PAGE_SETTING_FIELDS = ['pageTitle', 'metaDescription'];

const formatDate = (value) => new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));

export default function LandingPageEditor() {
  const [content, setContent] = useState(null);
  const [listings, setListings] = useState({});
  const [loadState, setLoadState] = useState('loading');
  const [preview, setPreview] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState([]);
  const [notice, setNotice] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [isDefault, setIsDefault] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [uploading, setUploading] = useState(() => new Set());
  const summaryRef = useRef(null);
  const noticeRef = useRef(null);
  const canvasRef = useRef(null);

  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      const res = await landingPageAPI.getContent();
      setContent(res.content);
      setListings(res.listings || {});
      setUpdatedAt(res.updatedAt);
      setIsDefault(res.isDefault);
      setEnabled(res.enabled !== false);
      setDirty(false);
      setLoadState('ready');
    } catch {
      setLoadState('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Errors switch back to editing, open the panel holding a bad field, and focus the summary
  useEffect(() => {
    if (!errors.length) return;
    setPreview(false);
    if (errors.some((e) => PAGE_SETTING_FIELDS.includes(e.field))) setSettingsOpen(true);
    summaryRef.current?.focus();
  }, [errors]);

  const onChange = useCallback((path, value) => {
    setContent((prev) => setAt(prev, path, value));
    setDirty(true);
  }, []);

  // Messages stay until dismissed (WCAG 2.2.1); errors also take focus
  const showNotice = useCallback((next) => {
    setNotice(next);
    if (next?.type === 'error') setTimeout(() => noticeRef.current?.focus(), 0);
  }, []);

  const onUploadImage = useCallback(async (path, file) => {
    const check = validateLogoFile(file, { maxBytes: IMAGE_MAX_BYTES });
    if (!check.ok) {
      showNotice({ type: 'error', text: check.message });
      return;
    }
    setUploading((prev) => new Set(prev).add(path));
    try {
      const { downloadUrl } = await uploadImageToS3(file, { variant: 'public' });
      if (!downloadUrl) throw new Error('The upload did not return an address');
      onChange(path, downloadUrl);
      showNotice({ type: 'info', text: 'Image uploaded. Save changes to publish it.' });
    } catch (err) {
      showNotice({ type: 'error', text: getLogoUploadErrorMessage(err, 'The image could not be uploaded.') });
    } finally {
      setUploading((prev) => {
        const next = new Set(prev);
        next.delete(path);
        return next;
      });
    }
  }, [onChange, showNotice]);

  const errorFor = useCallback((path) => errors.find((e) => e.field === path)?.message, [errors]);

  const ctx = {
    edit: !preview,
    content,
    onChange,
    errorFor,
    onUploadImage,
    uploading: (path) => uploading.has(path),
    announce: setAnnouncement
  };

  const applySaved = (res, message) => {
    setContent(res.content);
    setUpdatedAt(res.updatedAt);
    setIsDefault(res.isDefault);
    setErrors([]);
    setDirty(false);
    showNotice({ type: 'success', text: message });
  };

  const handleSave = async () => {
    setSaving(true);
    setNotice(null);
    try {
      applySaved(await landingPageAPI.saveContent(content), 'Home page saved. Visitors see the changes within a minute.');
    } catch (err) {
      const fieldErrors = err?.response?.data?.errors;
      if (Array.isArray(fieldErrors) && fieldErrors.length) {
        setErrors(fieldErrors);
      } else {
        showNotice({ type: 'error', text: err?.response?.data?.error || 'The home page could not be saved. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset the home page to the default text and layout? Unsaved and saved changes will be lost.')) return;
    setSaving(true);
    try {
      applySaved(await landingPageAPI.resetContent(), 'Home page reset to the default text and layout.');
    } catch {
      showNotice({ type: 'error', text: 'The home page could not be reset. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Not `disabled` while saving: that would drop keyboard focus off the switch
  const handleToggleEnabled = async () => {
    if (switching) return;
    const next = !enabled;
    if (!next && !window.confirm('Turn off the public home page? Visitors to the site address will go straight to the login page until you turn it back on.')) return;
    setSwitching(true);
    try {
      const res = await landingPageAPI.setEnabled(next);
      setEnabled(res.enabled);
      showNotice({
        type: 'success',
        text: res.enabled
          ? 'Home page turned on. Visitors see it again within a minute.'
          : 'Home page turned off. Visitors to the site address now go to the login page.'
      });
    } catch (err) {
      showNotice({ type: 'error', text: err?.response?.data?.error || 'The home page could not be switched on or off. Please try again.' });
    } finally {
      setSwitching(false);
    }
  };

  // In preview the page looks live, but its links must not leave the editor and lose changes.
  // A capture listener covers mouse and keyboard (Enter on a link fires click).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !preview) return undefined;
    const block = (e) => {
      if (e.target.closest?.('a')) {
        e.preventDefault();
        setAnnouncement('Links are turned off in preview. Use View live page to follow them.');
      }
    };
    canvas.addEventListener('click', block, true);
    return () => canvas.removeEventListener('click', block, true);
  }, [preview, loadState]);

  return (
    <div className="dashboard">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <AdminHeader />
      <div className="dashboard-layout">
        <AdminSidebar active="home-page" />
        <main id="main-content" className="dashboard-main" tabIndex={-1}>
          <div className="dashboard-content hpe-page">
            <div className="hpe-topbar">
              <div className="hpe-topbar-left">
                <h1 className="hpe-topbar-title">Home Page Editor</h1>
                {loadState === 'ready' && (
                  <span className="hpe-topbar-meta">
                    {dirty ? 'Unsaved changes' : isDefault ? 'Default text' : updatedAt ? `Saved ${formatDate(updatedAt)}` : ''}
                  </span>
                )}
              </div>
              {loadState === 'ready' && (
                <div className="hpe-topbar-right">
                  <button type="button" className={`hpe-toggle${preview ? ' is-on' : ''}`} aria-pressed={preview}
                    onClick={() => setPreview((p) => !p)}>
                    Preview
                  </button>
                  <button type="button" className="hpe-topbar-btn" aria-expanded={settingsOpen}
                    aria-controls="hpe-page-settings" onClick={() => setSettingsOpen((o) => !o)}>
                    Page settings
                  </button>
                  <a className="hpe-topbar-btn" href="/" target="_blank" rel="noopener noreferrer">
                    View live page<span className="sr-only"> (opens in a new tab)</span>
                  </a>
                  <div className="hpe-topbar-divider" aria-hidden="true" />
                  <button type="button" className="hpe-topbar-btn" onClick={handleReset} disabled={saving || isDefault}>
                    Reset to default
                  </button>
                  <button type="button" className="hpe-topbar-save" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              )}
            </div>

            <div className="sr-only" role="status" aria-live="polite">{announcement}</div>

            {notice && (
              <div ref={noticeRef} tabIndex={-1} className={`hpe-notice hpe-notice--${notice.type}`}
                role={notice.type === 'error' ? 'alert' : 'status'}>
                <p>{notice.text}</p>
                <button type="button" className="hpe-button hpe-button--light" onClick={() => setNotice(null)}>
                  Dismiss<span className="sr-only"> message</span>
                </button>
              </div>
            )}

            {errors.length > 0 && (
              <div ref={summaryRef} tabIndex={-1} className="hpe-summary" role="alert" aria-labelledby="hpe-summary-title">
                <h2 id="hpe-summary-title" className="hpe-summary-title">
                  {errors.length === 1 ? 'There is 1 problem to fix' : `There are ${errors.length} problems to fix`}
                </h2>
                <p>Nothing was saved.</p>
                <ul>
                  {errors.map((err) => (
                    <li key={`${err.field}-${err.message}`}>
                      <a
                        href={`#${fieldId(err.field)}`}
                        onClick={(e) => {
                          const target = document.getElementById(fieldId(err.field));
                          if (target) {
                            e.preventDefault();
                            target.focus();
                          }
                        }}
                      >
                        {err.message}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {loadState === 'loading' && <p role="status">Loading the home page…</p>}
            {loadState === 'error' && (
              <div className="hpe-notice hpe-notice--error" role="alert">
                <p>The home page content could not be loaded.</p>
                <button type="button" className="hpe-button hpe-button--light" onClick={load}>Try again</button>
              </div>
            )}

            {loadState === 'ready' && content && (
              <>
                <section className={`hpe-visibility${enabled ? '' : ' is-off'}`} aria-labelledby="hpe-visibility-title">
                  <div>
                    <h2 id="hpe-visibility-title" className="hpe-blocks-title">Public home page</h2>
                    <p id="hpe-visibility-desc" className="hpe-blocks-hint">
                      {enabled
                        ? 'On: visitors to the site address see this page.'
                        : 'Off: visitors to the site address go straight to the login page. You can still edit and save the page here.'}
                      {' '}Switching takes effect straight away and does not save your text changes.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    className="hpe-switch"
                    aria-checked={enabled}
                    aria-labelledby="hpe-visibility-title"
                    aria-describedby="hpe-visibility-desc"
                    aria-disabled={switching || undefined}
                    onClick={handleToggleEnabled}
                  >
                    <span className="hpe-switch-track" aria-hidden="true"><span className="hpe-switch-thumb" /></span>
                    <span className="hpe-switch-state" aria-hidden="true">{switching ? 'Saving…' : enabled ? 'On' : 'Off'}</span>
                  </button>
                </section>

                {settingsOpen && (
                  <section id="hpe-page-settings" className="hpe-panel" aria-labelledby="hpe-page-settings-title">
                    <h2 id="hpe-page-settings-title" className="hpe-panel-title">Page settings</h2>
                    <p className="hpe-panel-desc">Shown in the browser tab and in search results, not on the page.</p>
                    <EText ctx={{ ...ctx, edit: true }} path="pageTitle" label="Page title (required)" />
                    <EText ctx={{ ...ctx, edit: true }} path="metaDescription" label="Search description" multiline />
                  </section>
                )}

                {!preview && <BlocksManager ctx={ctx} />}

                <p className="hpe-hint-line">
                  {preview
                    ? 'Preview: this is how visitors see the page with your unsaved changes. Links are turned off here.'
                    : 'Edit any text, button or image where it sits. Empty buttons and text are hidden from visitors. Job fairs, employers and organizations come from live data.'}
                </p>

                <div className="hpe-canvas" ref={canvasRef}>
                  <div className={`home-page home-page--canvas${preview ? '' : ' is-editing'}`}>
                    <HomeHeader ctx={ctx} nextEvent={listings.nextEvent} user={null} level={2} as="div" navLabel="Home page menu" />
                    <div className="hp-main">
                      <HomeBody ctx={ctx} data={listings} level={2} />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
